import React, { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { GridItem, useMatchMedia, useBreakpoint } from '@nike/nike-design-system-components';
import { NikeSwoosh } from '@nike/nike-design-system-icons';
import loadable from '@loadable/component';
import TopBar from '../TopBar';
import DesktopShoppingMenu from '../DesktopShoppingMenu';
import UserTools from '../UserTools';
import Link from '../Link';
import {
  HOME_PAGE,
  PEAK_A_BOO_NAV,
  SCROLLING_DOWN,
  SCROLLING_UP,
  SCROLL,
  DESKTOP_TOP_HEIGHT,
  NAV_MAIN_SECTION_HEIGHT,
  EXPOSED_SEARCH_EXPERIENCES,
  GLOBAL_NAV_SEARCH,
  GLOBAL_NAV_MENU,
  BODEGA_DEFAULTS,
  PEAKABOO_HIDDEN_EXPERIENCES,
} from './../../constants';
import {
  StyledHeader,
  StyledDiv,
  StyledGrid,
  StyledNav,
  FixedSpacer,
  ExposedSearch,
  noPeekaboo,
  swooshSvg,
  swooshLink,
  swoosh,
  shoppingMenuGrid,
  exposedSearchHidden,
  exposedSearch,
} from './styles';
import useBodega from '../../utils/hooks/bodegaHook';
import useFeatureFlags from '../../utils/hooks/featureFlagsHook';
import { initializeWebShellIdentity } from '../../utils/identity';
import { AppContext } from '../../context/appContext';
import { AnalyticsContext } from '../../context/analyticsContext';
import { useNavOptionsContext } from '../../context/optionsContext';
import SkipToContent from '../SkipToContent';
import SkipToAccessibility from '../SkipToAccessibility';
import VisualSearch from '../VisualSearch';
import { getCloudBaseUrl } from '../../utils/url';
import useLocale from '../../utils/hooks/localeHook';
import CookieModal from '../CookieModal';
import useExternalComponents from '../../utils/externalComponents';
import createListener from '../../utils/listener';
import Scrim from '../Scrim';
import useGeoMismatch from '../../utils/hooks/geoMisMatchHook';
import useExperiment, { getExperiment } from '../../utils/hooks/experimentHook';
import { NikeAIVariantProvider } from '../../context/nikeAIVariantContext';

const GeoMismatchModal = loadable(() => import('../GeoMismatchModal'), {
  ssr: false,
});

function usePeakaboo(opts) {
  const peakabooEnabledFlag = useFeatureFlags(PEAK_A_BOO_NAV);
  // use peakabooNavEnabled from mount options if available
  const [peakabooEnabled, setPeakabooEnabled] = useState(
    opts.peakabooNavEnabled ?? peakabooEnabledFlag,
  );
  const experiments = useExperiment([{ name: 'global_nav_sticky' }]);
  useEffect(() => {
    const stickyExperiment = getExperiment(experiments, 'global_nav_sticky');
    if (
      stickyExperiment?.content?.experience === 'VARIANT' &&
      opts?.appInfo?.viewChannel === HOME_PAGE
    ) {
      setPeakabooEnabled(false);
    }
  }, [experiments]);

  return peakabooEnabled;
}

const Header = () => {
  const isMobile = useMatchMedia(useBreakpoint('lt', 'm'));
  const [, actions] = useContext(AppContext);
  const { opts, mounted } = useNavOptionsContext();
  const peakabooHidden = PEAKABOO_HIDDEN_EXPERIENCES.includes(opts?.appInfo?.viewChannel);
  const isCartPage = EXPOSED_SEARCH_EXPERIENCES.includes(opts?.appInfo?.viewChannel);
  const peakabooEnabled = usePeakaboo(opts);
  const searchLabel = useBodega('nav_header_label_search', BODEGA_DEFAULTS.SEARCH);
  const [headerClassNames, setHeaderClassNames] = useState({ isFixed: false, isHidden: false });
  const { mismatchedCountry, shouldShowGeoMismatch } = useGeoMismatch();
  const { analyticsClient } = useContext(AnalyticsContext);
  const [animateHeader, setAnimateHeader] = useState(false);
  const [showTopBar, setShowTopBar] = useState(true);

  // Fetch Nike AI experiment data for header icon (variant A)
  // 📝 ALTERNATIVE WITHOUT CONTEXT: This experiment fetch would remain here,
  // then pass nikeAIVariant as prop to UserTools: <UserTools nikeAIVariant={nikeAIVariant} />
  const experimentData = useExperiment([{ name: 'search_digital_coach' }]);
  const nikeAIVariant = React.useMemo(() => {
    if (!experimentData) return null;

    const experiment = getExperiment(experimentData, 'search_digital_coach');
    if (!experiment || !experiment.content) return null;

    // Use variant name directly from optimization platform
    return experiment.content.variant;
  }, [experimentData]);

  const { isFixed, isHidden } = headerClassNames;

  const [headerContainerClassName, setHeaderContainerClassName] = useState(
    peakabooEnabled ? '' : noPeekaboo,
  );

  const headerClassName = {
    'is-fixed': isFixed && !peakabooHidden,
    'is-hidden': isHidden,
    'no-animation': !animateHeader,
    [exposedSearchHidden]: isHidden && isCartPage && exposedSearchHidden,
  };
  const headerClassRef = useRef('');
  // used to close shopping menu dropdowns more consistently
  const topBarRef = useRef(null);
  const navAllyLabel = useBodega('nav_header_aria_label', 'Global Navigation');

  const isNavMenuEnabled = useFeatureFlags(GLOBAL_NAV_MENU);
  const isSearchEnabled = useFeatureFlags(GLOBAL_NAV_SEARCH);

  const locale = useLocale();
  const swooshHref = getCloudBaseUrl(locale);
  const displayListener = useRef(createListener());

  let lastScrollY = 0;

  if (typeof window !== 'undefined') {
    lastScrollY = window.scrollY;
  }
  const updateScrollDirection = () => {
    const scrollY = window.scrollY;
    const scrollDir = scrollY > lastScrollY ? SCROLLING_DOWN : SCROLLING_UP;
    lastScrollY = scrollY > 0 ? scrollY : 0;
    return scrollDir;
  };

  useEffect(() => {
    // waiting for mounted here to make sure the NikeDotcomNav interface is available and ready
    if (mounted) {
      const setComponent = useExternalComponents('header');
      setComponent({
        isPeakabooNavEnabled: () => peakabooEnabled,
        onDisplayChange: displayListener.current.on,
      });
    }
  }, [peakabooEnabled, mounted]);

  useEffect(() => {
    if (peakabooEnabled) {
      const onScroll = () => {
        const updatedScrollDirection = updateScrollDirection();
        /* We are not setting any class name as we want both top bar and header to be visible
           when offset is less than 60
        */
        if (window.scrollY < NAV_MAIN_SECTION_HEIGHT && updatedScrollDirection !== SCROLLING_DOWN) {
          if (headerClassRef.current.isFixed || headerClassRef.current.isHidden) {
            let isFixed = true,
              isHidden = false;
            if (window.scrollY < DESKTOP_TOP_HEIGHT) {
              isHidden = false;
              setAnimateHeader(false);

              if (window.scrollY === 0 || !isMobile) {
                isFixed = false;
              }
            } else {
              setAnimateHeader(true);
            }

            displayListener.current.call({ isFixed, isHidden });
            setHeaderClassNames({ isFixed, isHidden });

            headerClassRef.current = { isFixed, isHidden };
          }
        }

        // When user scrolls down we want to hide header when offset is greater than 36
        if (updatedScrollDirection === SCROLLING_DOWN && window.scrollY > DESKTOP_TOP_HEIGHT) {
          if (!headerClassRef.current.isFixed || !headerClassRef.current.isHidden) {
            displayListener.current.call({ isFixed: true, isHidden: true });
            setHeaderClassNames({ isFixed: true, isHidden: true });
          }
          headerClassRef.current = { isFixed: true, isHidden: true };

          if (window.scrollY < NAV_MAIN_SECTION_HEIGHT) {
            setAnimateHeader(false);
          } else {
            setAnimateHeader(true);
          }
        }
        // When user scrolls up we want to show main menu and brand menu all the time
        if (updatedScrollDirection === SCROLLING_UP && window.scrollY > NAV_MAIN_SECTION_HEIGHT) {
          if (!headerClassRef.current.isFixed || headerClassRef.current.isHidden) {
            displayListener.current.call({ isFixed: true, isHidden: false });
            setHeaderClassNames({ isFixed: true, isHidden: false });
          }
          headerClassRef.current = { isFixed: true, isHidden: false };
        }
      };
      window.addEventListener(SCROLL, onScroll);

      return () => window.removeEventListener(SCROLL, onScroll);
    } else {
      // when peekaboo is disabled for a geo we want header to be fixed
      if (headerClassRef.current.isFixed || headerClassRef.current.isHidden) {
        displayListener.current.call({ isFixed: false, isHidden: false });
      }
      setHeaderContainerClassName(noPeekaboo);
      setHeaderClassNames({ isFixed: false, isHidden: false });
      headerClassRef.current = { isFixed: false, isHidden: false };
      const onScroll = () => {
        setShowTopBar(window.scrollY < DESKTOP_TOP_HEIGHT);
      };
      window.addEventListener(SCROLL, onScroll);

      return () => window.removeEventListener(SCROLL, onScroll);
    }
  }, [peakabooEnabled]);

  useEffect(() => {
    initializeWebShellIdentity(actions.setIsLoggedIn, actions.setUserProfile);
  }, []);

  let showExposedSearch = isCartPage && isMobile;

  const homePageAria = useBodega('nav_header_label_nikeSwooshLogo', BODEGA_DEFAULTS.HOME_PAGE);

  return (
    // 📝 ALTERNATIVE WITHOUT CONTEXT: Remove this Provider wrapper entirely
    <NikeAIVariantProvider variant={nikeAIVariant}>
      <StyledNav
        aria-label={navAllyLabel}
        className={clsx(
          headerContainerClassName,
          showExposedSearch ? exposedSearch : '',
          !showTopBar && 'topbar-hidden',
          'pre-l-header-container',
        )}
      >
        <CookieModal />
        {shouldShowGeoMismatch && (
          <GeoMismatchModal
            appOptions={{ opts, mounted }}
            locale={locale}
            mismatchedCountry={mismatchedCountry}
          />
        )}
        <SkipToContent />
        <SkipToAccessibility />
        <TopBar ref={topBarRef} />
        <StyledHeader className={clsx(headerClassName)}>
          <StyledDiv>
            <StyledGrid alignItems="center" data-testid="grid-container">
              <GridItem className={swoosh} m={2} xs={5}>
                <Link
                  ariaLabel={homePageAria}
                  className={swooshLink}
                  href={swooshHref}
                  onClick={() => {
                    analyticsClient?.clickHeaderLogo({ dataset: { brand: 'nike' } });
                  }}
                >
                  <NikeSwoosh className={swooshSvg} />
                </Link>
              </GridItem>
              <GridItem className={shoppingMenuGrid} m={7} xs={1}>
                {isNavMenuEnabled && <DesktopShoppingMenu topBarRef={topBarRef} />}
              </GridItem>
              <GridItem m={3} xs={6}>
                <UserTools showExposedSearch={showExposedSearch} />
              </GridItem>
            </StyledGrid>
            {isNavMenuEnabled && showExposedSearch && isSearchEnabled && (
              <ExposedSearch data-testid="exposed-search-container" searchLabel={searchLabel}>
                <VisualSearch />
              </ExposedSearch>
            )}
          </StyledDiv>
        </StyledHeader>
        <Scrim />
      </StyledNav>
      {!peakabooEnabled && <FixedSpacer />}
      <div id="skip-to-content"></div>
      <div id="accessibility-at-nike"></div>
    </NikeAIVariantProvider>
  );
};

export default Header;
